import { useAuthStore } from "../auth/authStore";
import { AUTH_API_PREFIX } from "../auth/authApiPaths";
import { refreshSession, type SessionRefreshOutcome } from "../auth/sessionRefresh";
import { atlasWs } from "./ws";

/** Same-origin auth URLs (never wrap with `/api/v1`). Boundary-safe for `/api/v1/auth`. */
function isBareAuthApiUrl(path: string): boolean {
  return (
    path === "/auth" ||
    path.startsWith("/auth/") ||
    path === AUTH_API_PREFIX ||
    path.startsWith(`${AUTH_API_PREFIX}/`)
  );
}

function reconnectAtlasWs(): void {
  if (useAuthStore.getState().accessToken) atlasWs.connect();
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function refreshAccessToken(): Promise<SessionRefreshOutcome> {
  const outcome = await refreshSession();
  if (outcome === "ok") reconnectAtlasWs();
  return outcome;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url =
    path.startsWith("http")
      ? path
      : isBareAuthApiUrl(path) || path.startsWith("/.well-known")
        ? path
        : `/api/v1${path.startsWith("/") ? path : `/${path}`}`;
  const token = useAuthStore.getState().accessToken;

  const headers: HeadersInit = {
    Accept: "application/json",
    ...(init.headers || {}),
  };
  if (init.body && !(init.body instanceof FormData) && !(headers as Record<string, string>)["Content-Type"]) {
    (headers as Record<string, string>)["Content-Type"] = "application/json";
  }
  if (token) {
    (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
  }

  let res = await fetch(url, { ...init, headers });

  if (res.status === 401 && !init.signal?.aborted) {
    const outcome = await refreshAccessToken();
    if (outcome === "ok") {
      const t2 = useAuthStore.getState().accessToken;
      (headers as Record<string, string>)["Authorization"] = `Bearer ${t2}`;
      res = await fetch(url, { ...init, headers });
    } else if (outcome === "rejected") {
      // Only a refused refresh credential ends the session; a network blip
      // surfaces as this request's error and the next call retries.
      useAuthStore.getState().clearSession();
      try {
        localStorage.removeItem("atlas-auth-storage");
      } catch {
        /* ignore */
      }
    }
  }

  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(body || res.statusText, res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** GET binary response and trigger browser download. */
export async function apiDownload(path: string, filename: string, init?: RequestInit): Promise<void> {
  const url = path.startsWith("http")
    ? path
    : `/api/v1${path.startsWith("/") ? path : `/${path}`}`;
  const token = useAuthStore.getState().accessToken;
  const headers: HeadersInit = { ...(init?.headers || {}) };
  if (token) {
    (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
  }
  let res = await fetch(url, { ...init, method: "GET", headers });
  if (res.status === 401) {
    const outcome = await refreshAccessToken();
    if (outcome === "ok") {
      const t2 = useAuthStore.getState().accessToken;
      (headers as Record<string, string>)["Authorization"] = `Bearer ${t2}`;
      res = await fetch(url, { ...init, method: "GET", headers });
    }
  }
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(body || res.statusText, res.status, body);
  }
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export const api = {
  get: <T>(path: string, init?: RequestInit) => apiFetch<T>(path, { ...init, method: "GET" }),
  post: <T>(path: string, body?: unknown, init?: RequestInit) =>
    apiFetch<T>(path, {
      ...init,
      method: "POST",
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body?: unknown, init?: RequestInit) =>
    apiFetch<T>(path, {
      ...init,
      method: "PATCH",
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  put: <T>(path: string, body?: unknown, init?: RequestInit) =>
    apiFetch<T>(path, {
      ...init,
      method: "PUT",
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  delete: <T>(path: string, init?: RequestInit) => apiFetch<T>(path, { ...init, method: "DELETE" }),
};
